import React, { useEffect, useRef, useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhButton from "../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import {
  createBulkStageJob,
  downloadBulkStageFailedReport,
  downloadBulkStageSample,
  getBulkStageJobProgress,
} from "../../../../services/bulkStageJobService";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  organizationId?: string;
};

const BulkStatusUploadModal = ({
  open,
  onClose,
  onUpdated,
  organizationId,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState<string>("");
  const [progress, setProgress] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      clearPoll();
      setFile(null);
      setUploading(false);
      setJobId("");
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
    return clearPoll;
  }, [open]);

  const startPolling = (id: string) => {
    clearPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await getBulkStageJobProgress(id);
        const data = res?.data || res;
        setProgress(data);
        const status = String(data?.status || "");
        if (["completed", "failed", "partial"].includes(status)) {
          clearPoll();
          setUploading(false);
          if (status === "completed") {
            openSuccessNotification(
              data?.progress?.message || "Bulk stage update completed"
            );
            onUpdated?.();
          } else if (status === "partial") {
            openSuccessNotification(
              data?.progress?.message || "Bulk stage update finished with some failures"
            );
            onUpdated?.();
          } else {
            openErrorNotification(
              data?.errorMessage || data?.progress?.message || "Bulk stage update failed"
            );
          }
        }
      } catch (e: any) {
        clearPoll();
        setUploading(false);
        openErrorNotification(e?.message || "Failed to poll progress");
      }
    }, 1500);
  };

  const onDownloadSample = async () => {
    try {
      await downloadBulkStageSample();
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to download sample");
    }
  };

  const onUpload = async () => {
    if (!file) {
      openErrorNotification("Choose an .xlsx file first");
      return;
    }
    setUploading(true);
    setProgress(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (organizationId) formData.append("organizationId", organizationId);
      const res = await createBulkStageJob(formData);
      const job = res?.data || res;
      const id = String(job?._id || "");
      if (!id) throw new Error("Upload did not return a job id");
      setJobId(id);
      setProgress(job);
      openSuccessNotification("Upload received — updating stages in the background");
      startPolling(id);
    } catch (e: any) {
      setUploading(false);
      openErrorNotification(e?.message || "Upload failed");
    }
  };

  const onDownloadFailed = async () => {
    if (!jobId) return;
    try {
      await downloadBulkStageFailedReport(jobId);
    } catch (e: any) {
      openErrorNotification(e?.message || "No failed report available");
    }
  };

  const percent = Number(progress?.progress?.percent || 0);
  const status = String(progress?.status || "");
  const done = ["completed", "failed", "partial"].includes(status);
  const totals = progress?.totals || {};

  return (
    <YhModal
      open={open}
      onClose={uploading && !done ? undefined : onClose}
      title="Bulk status upload"
      width={560}
      closable={!uploading || done}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-[#6B7280]">
          Upload an Excel file to update lead <span className="font-semibold text-[#111827]">stage / status</span> in
          bulk. Processing runs in the background so large files stay fast.
        </p>

        <div className="rounded-xl border border-[#EEF0F5] bg-[#F8F9FF] px-4 py-3 text-xs text-[#4B5563]">
          <p className="font-semibold text-[#111827]">Required columns</p>
          <p className="mt-1">
            <code>stage</code> + at least one of{" "}
            <code>lead_id</code> (e.g. LD3657) or <code>phone</code>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <YhButton variant="secondary" onClick={onDownloadSample} disabled={uploading && !done}>
            Download sample.xlsx
          </YhButton>
          <button
            type="button"
            className="inline-flex h-10 items-center rounded-xl border border-dashed border-[#D4B8C0] bg-white px-4 text-sm font-semibold text-[#A02448] hover:bg-[#F9F0F4]"
            onClick={() => inputRef.current?.click()}
            disabled={uploading && !done}
          >
            {file ? file.name : "Choose .xlsx file"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        {progress ? (
          <div className="rounded-xl border border-[#EEF0F5] bg-white px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-2 text-xs">
              <span className="font-semibold uppercase tracking-wide text-[#6B7280]">
                {status || "processing"}
              </span>
              <span className="font-semibold text-[#A02448]">{percent}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#EEF0F5]">
              <div
                className="h-full rounded-full bg-[#A02448] transition-all"
                style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-[#374151]">
              {progress?.progress?.message || "Working…"}
            </p>
            {done ? (
              <p className="mt-1 text-xs text-[#6B7280]">
                Total {totals.total || 0} · Updated {totals.success || 0} · Failed{" "}
                {totals.fail || 0} · Skipped {totals.skipped || 0}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2 flex justify-end gap-2">
          <YhButton
            variant="secondary"
            onClick={onClose}
            disabled={uploading && !done}
          >
            {done ? "Close" : "Cancel"}
          </YhButton>
          {done && (totals.fail > 0 || totals.skipped > 0) ? (
            <YhButton variant="secondary" onClick={onDownloadFailed}>
              Download failed rows
            </YhButton>
          ) : null}
          {!done ? (
            <YhButton onClick={onUpload} disabled={uploading || !file}>
              {uploading ? "Processing…" : "Upload & update"}
            </YhButton>
          ) : null}
        </div>
      </div>
    </YhModal>
  );
};

export default BulkStatusUploadModal;
